import { useState, useRef } from 'react';

import { css, keyframes } from '@compiled/react';
import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import ChevronLeftIcon from '@atlaskit/icon/core/chevron-left';
import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';

import { useGlobalNavUserProfileClickActions } from '@/components/global-nav/user-profile-new/src/user-profile';
import { wacToken } from '@/libraries/design/foundations';
import { useLocalizedHref } from '@/libraries/web-framework/src/localization/use-localized-href';
import { ProductKeyTypes } from '@/libraries/wpl/foundations/src/data-event-props';
import { LogoV3 as Logo } from '@/libraries/wpl/foundations/src/logo-v3';
import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@/utils/feature-flags/feature-flag-manifest';
import { useIsMobile } from '@/utils/useIsMobile';

import { AiContent } from '../ai-content';
import AutoCompleteSearchBar from '../auto-complete-search-bar';
import { ContactSalesCta } from '../components/contact-sales-cta';
import { useMobileNavDisplay } from '../hooks/use-mobile-nav-display';
import { ProductFeaturedCollectionSubnav } from '../product-featured-collection-subnav';
import { SkipToContent } from '../skip-to-content';
import { SolutionsSubnav } from '../solutions-subnav';

import { MobileNavLinks } from './mobile-nav-links-container';
import MobileLoggedInUseDropdown from './mobile-nav-logged-in-dropdown';
import { MobileNavProductBusinessTeams } from './mobile-nav-product-business-teams';
import { MobileNavProductDevelopers } from './mobile-nav-product-developers';
import { MobileNavProductITProfessionals } from './mobile-nav-product-it-professionals';
import { MobileNavProductLeadershipTeams } from './mobile-nav-product-leadership-teams';
import { MobileNavProductProductManagers } from './mobile-nav-product-managers';
import { MobileNavResources } from './mobile-nav-resources';
import { MobileNavTop } from './mobile-nav-top';
import { MobileNavWhyAtlassian } from './mobile-nav-why-atlassian';

import type { MobileNavLinksContainerInfo } from './mobile-nav-links-container';

const containerCss = css({
	background: wacToken('color.neutral.light0'),
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.150'),
});

const shownInnerMobileDropdownMenuStyle = css({
	display: 'block',
	opacity: 1,
	transition: 'opacity 0.4s ease-in 200ms forwards',
	margin: `${token('space.100')} ${token('space.400')}`,
	[media.below.sm]: {
		margin: `${token('space.100')} ${token('space.100')}`,
	},
});

const hiddenInnerMobileDropdownMenuStyle = css({
	display: 'none',
	opacity: 0,
	transition: 'opacity 0.4s ease-in 200ms forwards',
	margin: `${token('space.100')} ${token('space.400')}`,
});

const menuContentStyle = css({
	display: 'none',
	width: 'auto',

	[media.above.sm]: {
		display: 'none',
	},
});

const containerClosedCss = css({
	padding: token('space.200'),
});

const containerOpenCss = css({
	padding: '24px',
	[media.below.sm]: {
		height: '100vh',
		width: '100%',
		overflow: 'auto',
		position: 'fixed',
		insetBlockStart: '0',
		padding: '24px',
		zIndex: '5',
	},
});

const navBarClosedCss = css({
	paddingInline: token('space.200'),
	display: 'flex',
	justifyContent: 'space-between',
});

const navBarOpenCss = css({
	[media.above.sm]: {
		paddingInline: token('space.200'),
	},
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
});

const slideInLeftKeyframes = keyframes({
	from: {
		transform: 'translateX(-100%)',
		opacity: 0,
	},
	to: {
		transform: 'translateX(0%)',
		opacity: 1,
	},
});

const slideInDown = keyframes({
	from: {
		transform: 'translateY(-24px)',
		opacity: '1',
	},
	to: {
		transform: 'translateY(24px)',
		opacity: '1',
	},
});

const slideInUp = keyframes({
	from: {
		transform: 'translateY(24px)',
		opacity: '1',
	},
	to: {
		transform: 'translateY(-10px)',
		opacity: '1',
	},
});

const slideInRight = keyframes({
	from: {
		transform: 'translateX(100%)',
		opacity: 0,
	},
	to: {
		transform: 'translateX(0%)',
		opacity: 1,
	},
});

const defaultInnerMenuCss = css({
	opacity: 0,
	transition: 'opacity 0.4s ease-in 200ms forwards',
	[`&.fade-in`]: {
		opacity: 1,
	},
	[`&.slide-in-left`]: {
		opacity: 1,
	},
});

const slideInLeftCss = css({
	animation: `0.5s ${slideInLeftKeyframes} forwards`,
});

const slideInDownCss = css({
	opacity: '0',
	animation: `0.5s ${slideInDown} forwards`,
});

const slideInUpCss = css({
	opacity: '0',
	animation: `0.5s ${slideInUp} forwards`,
});

const slideInRightCss = css({
	animation: `0.5s ${slideInRight} forwards`,
});

const backButtonCss = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.100'),
	marginInlineStart: token('space.050'),
	marginBlockStart: token('space.100'),
	marginBlockEnd: token('space.100'),
	[media.above.sm]: {
		marginInlineStart: token('space.300'),
	},
});

export type DropdownType =
	| 'default'
	| 'devops'
	| 'categories'
	| 'product-everyone'
	| 'product-developer'
	| 'product-product-managers'
	| 'product-it-professionals'
	| 'product-business-teams'
	| 'product-leadership-teams'
	| 'teams'
	| 'solutions'
	| 'ai'
	| 'why-atlassian'
	| 'resources'
	| 'search'
	| 'why-bitbucket';

export type MobileGlobalNavProps = {
	productKey: ProductKeyTypes;
	isShowSearchButton?: boolean;
};

export type MobileGlobalNavMenuProps = {
	isDisplayLoggedInUseDropdown: boolean;
};

export type MobileGlobalNavDropdownMenuProps = {
	readonly showMenu: boolean;
	readonly isUserLoggedIn: boolean;
	readonly menuPage: DropdownType;
	readonly firstOpen: boolean;
	readonly mobileLinksSections: MobileNavLinksContainerInfo[];
	handleToggleMenu: (showMenu: boolean) => void;
	handleMenuPage: (menuPage: DropdownType | string) => void;
	handleFirstOpen: (firstOpen: boolean) => void;
	productKey?: ProductKeyTypes;
};

export const MobileGlobalNavDropdownMenu = ({
	isUserLoggedIn,
	menuPage = 'default',
	firstOpen,
	mobileLinksSections,
	handleMenuPage,
	handleFirstOpen,
	productKey = 'home',
}: // eslint-disable-next-line complexity
MobileGlobalNavDropdownMenuProps) => {
	const intl = useIntl();
	const isNavigatingBackRef = useRef(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isMobile = useIsMobile();
	const isAiTabEnabled = checkFeatureFlagGate(featureFlags.team26EuRefresh);

	return (
		<>
			{menuPage === 'default' && isMobile ? (
				<div
					css={[css({ paddingBlockStart: '24px' }), isNavigatingBackRef.current && slideInUpCss]}
				>
					<AutoCompleteSearchBar
						key="default-search-bar"
						isMobileView={true}
						readOnly={true}
						parentType="GlobalNav"
						menuPage={menuPage}
						fullWidth={true}
						onFocus={() => {
							if (!isNavigatingBackRef.current) {
								handleMenuPage('search');
							}
							createAnalyticsEvent({
								eventName: 'searchOpen',
								eventType: 'ui',
								action: 'clicked',
								actionSubject: 'button',
								actionSubjectId: 'searchButtonMobileV2',
								source: 'globalNav',
								attributes: {
									product: 'marketing',
									pageUrl: window.location.href,
									pagePath: window.location.pathname,
								},
							}).fire();
						}}
					/>
				</div>
			) : (
				<></>
			)}
			{(menuPage === 'resources' ||
				menuPage === 'product-everyone' ||
				menuPage === 'product-developer' ||
				menuPage === 'product-product-managers' ||
				menuPage === 'product-it-professionals' ||
				menuPage === 'product-business-teams' ||
				menuPage === 'product-leadership-teams' ||
				menuPage === 'solutions' ||
				menuPage === 'ai' ||
				menuPage === 'why-atlassian') && (
				<div css={[backButtonCss]}>
					<button
						style={{ all: 'unset', width: 'fit-content' }}
						onClick={(e) => {
							e.preventDefault();
							e.stopPropagation();
							isNavigatingBackRef.current = true;
							handleFirstOpen(false);
							handleMenuPage('default');

							// reset the flag after navigation
							setTimeout(() => {
								isNavigatingBackRef.current = false;
							}, 100);
						}}
					>
						<div
							css={[
								slideInLeftCss,
								css({
									color: token('color.text.selected'),
									display: 'flex',
									alignItems: 'center',
								}),
							]}
						>
							<ChevronLeftIcon label="" spacing="spacious" />
							<span>
								{intl.formatMessage({
									id: 'globalnavv2.menu-back',
									defaultMessage: 'Back',
								})}
							</span>
						</div>
					</button>
				</div>
			)}
			<div
				css={[
					defaultInnerMenuCss,
					!firstOpen && slideInRightCss,
					!firstOpen && slideInLeftCss,
					menuPage === 'default'
						? shownInnerMobileDropdownMenuStyle
						: hiddenInnerMobileDropdownMenuStyle,
				]}
				className={`firstopen inner-mobile-dropdown-menu ${
					firstOpen ? 'fade-in' : 'slide-in-left'
				}`}
			>
				<ContactSalesCta isFullWidth={true} />
				<MobileNavLinks
					isLoggedIn={isUserLoggedIn}
					handleMenuPage={handleMenuPage}
					mobileLinksSections={mobileLinksSections}
					productKey={productKey}
				/>
			</div>
			<div
				css={[
					slideInRightCss,
					menuPage === 'product-everyone'
						? shownInnerMobileDropdownMenuStyle
						: hiddenInnerMobileDropdownMenuStyle,
				]}
				className="product-everyone-inner inner-mobile-dropdown-menu"
			>
				<ProductFeaturedCollectionSubnav isMobile={true} />
			</div>
			<div
				css={[
					slideInRightCss,
					menuPage === 'product-developer'
						? shownInnerMobileDropdownMenuStyle
						: hiddenInnerMobileDropdownMenuStyle,
				]}
				className="product-developer-inner inner-mobile-dropdown-menu"
			>
				<MobileNavProductDevelopers />
			</div>
			<div
				css={[
					slideInRightCss,
					menuPage === 'product-product-managers'
						? shownInnerMobileDropdownMenuStyle
						: hiddenInnerMobileDropdownMenuStyle,
				]}
				className="product-managers-inner inner-mobile-dropdown-menu"
			>
				<MobileNavProductProductManagers />
			</div>
			<div
				css={[
					slideInRightCss,
					menuPage === 'product-it-professionals'
						? shownInnerMobileDropdownMenuStyle
						: hiddenInnerMobileDropdownMenuStyle,
				]}
				className="inner-mobile-dropdown-menu"
			>
				<MobileNavProductITProfessionals />
			</div>
			<div
				css={[
					slideInRightCss,
					menuPage === 'product-business-teams'
						? shownInnerMobileDropdownMenuStyle
						: hiddenInnerMobileDropdownMenuStyle,
				]}
				className="inner-mobile-dropdown-menu"
			>
				<MobileNavProductBusinessTeams />
			</div>
			<div
				css={[
					slideInRightCss,
					menuPage === 'product-leadership-teams'
						? shownInnerMobileDropdownMenuStyle
						: hiddenInnerMobileDropdownMenuStyle,
				]}
				className="inner-mobile-dropdown-menu"
			>
				<MobileNavProductLeadershipTeams />
			</div>
			<div
				css={[
					slideInRightCss,
					menuPage === 'teams' || menuPage === 'solutions'
						? shownInnerMobileDropdownMenuStyle
						: hiddenInnerMobileDropdownMenuStyle,
				]}
				className="inner-mobile-dropdown-menu"
			>
				<SolutionsSubnav />
			</div>
			{isAiTabEnabled && (
				<div
					css={[
						slideInRightCss,
						menuPage === 'ai'
							? shownInnerMobileDropdownMenuStyle
							: hiddenInnerMobileDropdownMenuStyle,
					]}
					className="inner-mobile-dropdown-menu"
				>
					<AiContent productKey={productKey} />
				</div>
			)}
			<div
				css={[
					slideInRightCss,
					menuPage === 'why-atlassian'
						? shownInnerMobileDropdownMenuStyle
						: hiddenInnerMobileDropdownMenuStyle,
				]}
				className="inner-mobile-dropdown-menu"
			>
				<MobileNavWhyAtlassian productKey={productKey} />
			</div>
			<div
				css={[
					slideInRightCss,
					menuPage === 'resources'
						? shownInnerMobileDropdownMenuStyle
						: hiddenInnerMobileDropdownMenuStyle,
				]}
				className="inner-mobile-dropdown-menu"
			>
				<MobileNavResources productKey={productKey} />
			</div>
			{menuPage === 'search' && (
				<div
					key="search-page"
					css={[
						menuPage === 'search'
							? shownInnerMobileDropdownMenuStyle
							: hiddenInnerMobileDropdownMenuStyle,
					]}
					className="searchpageinnermenuinner-mobile-dropdown-menu"
				>
					<div
						css={css({
							display: 'flex',
							flexDirection: 'column',
						})}
					>
						{/* Back button below the divider */}
						<button
							style={{ all: 'unset', width: 'fit-content' }}
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
								isNavigatingBackRef.current = true;
								handleFirstOpen(false);
								handleMenuPage('default');

								// reset the flag after navigation
								setTimeout(() => {
									isNavigatingBackRef.current = false;
								}, 100);
							}}
						>
							<div
								css={[
									slideInLeftCss,
									css({
										color: token('color.text.selected'),
										display: 'flex',
										alignItems: 'center',
										paddingBlockStart: '16px',
									}),
								]}
							>
								<ChevronLeftIcon label="" spacing="spacious" />
								<span>
									{intl.formatMessage({
										id: 'globalnavv2.menu-back',
										defaultMessage: 'Back',
									})}
								</span>
							</div>
						</button>
						{/* Search bar below back button */}
						<div css={[slideInDownCss]}>
							<AutoCompleteSearchBar
								key="search-page-search-bar"
								isMobileView={true}
								parentType="GlobalNav"
								menuPage={menuPage}
								fullWidth={true}
							/>
						</div>
					</div>
				</div>
			)}
		</>
	);
};

export const MobileGlobalNav = ({ productKey, isShowSearchButton }: MobileGlobalNavProps) => {
	const intl = useIntl();
	const [isOpenSearch, setIsOpenSearch] = useState(false);

	const teamsText = intl.formatMessage({
		id: 'globalNavigation.Navlink.solutions',
		defaultMessage: 'Solutions',
	});
	const teamsKeyAndDropdownType = 'solutions';

	const {
		userInfo,
		showMenu,
		isDisplayLoggedInUseDropdown,
		firstOpen,
		menuPage,
		handleToggleMenu,
		handleFirstOpen,
		handleMenuPage,
		handleClickAvatar,
	} = useMobileNavDisplay(isOpenSearch);

	const { onSwitchAccountClick, onManageProfileClick, onManageLicensesClick, onLogOutClick } =
		useGlobalNavUserProfileClickActions({ email: userInfo.email });

	const isAiTabEnabled = checkFeatureFlagGate(featureFlags.team26EuRefresh);

	const mobileLinksSections: MobileNavLinksContainerInfo[] = [
		{
			mobileLinkKey: 'product-nav-links',
			mobileLinksProps: [
				{
					globalLinksKey: 'products',
					linkText: intl.formatMessage({
						id: 'globalnavv2.navlink.products',
						defaultMessage: 'Products',
					}),
					hasSubnav: false,
				},
				{
					globalLinksKey: 'everyone',
					linkText: intl.formatMessage({
						id: 'globalnavv2.navlink.featured',
						defaultMessage: 'Featured',
					}),
					hasSubnav: true,
					dropdownType: 'product-everyone',
				},
				{
					globalLinksKey: 'developers',
					linkText: intl.formatMessage({
						id: 'globalnavv2.navlink.developers',
						defaultMessage: 'Developers',
					}),
					hasSubnav: true,
					dropdownType: 'product-developer',
				},
				{
					globalLinksKey: 'productmanagers',
					linkText: intl.formatMessage({
						id: 'globalnavv2.navlink.productmanagers',
						defaultMessage: 'Product Managers',
					}),
					hasSubnav: true,
					dropdownType: 'product-product-managers',
				},
				{
					globalLinksKey: 'itprofessionals',
					linkText: intl.formatMessage({
						id: 'globalnavv2.navlink.itprofessionals',
						defaultMessage: 'IT professionals',
					}),
					hasSubnav: true,
					dropdownType: 'product-it-professionals',
				},
				{
					globalLinksKey: 'businessteams',
					linkText: intl.formatMessage({
						id: 'globalnavv2.navlink.businessteams',
						defaultMessage: 'Business Teams',
					}),
					hasSubnav: true,
					dropdownType: 'product-business-teams',
				},
				{
					globalLinksKey: 'leadershipteams',
					linkText: intl.formatMessage({
						id: 'globalnavv2.navlink.leadershipteams',
						defaultMessage: 'Leadership Teams',
					}),
					hasSubnav: true,
					dropdownType: 'product-leadership-teams',
				},
			],
		},
		{
			mobileLinkKey: 'other-nav-links',
			mobileLinksProps: [
				...(isAiTabEnabled
					? [
							{
								globalLinksKey: 'ai',
								linkText: intl.formatMessage({
									id: 'globalnavv2.navlink.ai',
									defaultMessage: 'AI',
								}),
								hasSubnav: true,
								dropdownType: 'ai',
							},
						]
					: []),

				{
					globalLinksKey: teamsKeyAndDropdownType,
					linkText: teamsText,
					hasSubnav: true,
					dropdownType: teamsKeyAndDropdownType,
				},
				{
					globalLinksKey: 'why-atlassian',
					linkText: intl.formatMessage({
						id: 'globalnavv2.navlink.whyAtlassian',
						defaultMessage: 'Why Atlassian',
					}),
					hasSubnav: true,
					dropdownType: 'why-atlassian',
				},
				{
					globalLinksKey: 'resources',
					linkText: intl.formatMessage({
						id: 'globalnavv2.navlink.resources',
						defaultMessage: 'Resources',
					}),
					hasSubnav: true,
					dropdownType: 'resources',
				},
				{
					globalLinksKey: 'enterprise',
					linkText: intl.formatMessage({
						id: 'globalnavv2.navlink.enterprise',
						defaultMessage: 'Enterprise',
					}),
					hasSubnav: false,
					href: useLocalizedHref('/enterprise'),
				},
			],
		},
	];

	const atlassianLogo = (
		<Logo brand="atlassian" type="logo" appearance="brand" height={20} width={156} />
	);

	const mobileGlobalNavDropdownMenu = () => (
		<MobileGlobalNavDropdownMenu
			showMenu={showMenu}
			isUserLoggedIn={userInfo.isUserLoggedIn}
			menuPage={menuPage as DropdownType}
			firstOpen={firstOpen}
			mobileLinksSections={mobileLinksSections}
			handleToggleMenu={handleToggleMenu}
			handleMenuPage={handleMenuPage}
			handleFirstOpen={handleFirstOpen}
			productKey={productKey}
		/>
	);

	return (
		<nav css={[containerCss, showMenu ? containerOpenCss : containerClosedCss]}>
			<SkipToContent />
			<div css={[showMenu ? navBarOpenCss : navBarClosedCss]}>
				<MobileNavTop
					productKey={productKey}
					isLoggedIn={userInfo.isUserLoggedIn}
					userName={userInfo.name ?? ''}
					picture={userInfo.picture ?? ''}
					handleClickAvatar={handleClickAvatar}
					showMenu={showMenu}
					handleToggleMenu={handleToggleMenu}
					menuPage={menuPage as DropdownType}
					handleMenuPage={handleMenuPage}
					handleFirstOpen={handleFirstOpen}
					mobileLogo={atlassianLogo}
					mobileLogoAriaLabel={intl.formatMessage({
						id: 'globalnavv2.logo-label',
						defaultMessage: 'Atlassian logo',
					})}
					mobileLogoHref={'/'}
					mobileLogoDataEventContainer={'globalNavWacV2'}
					isShowSearchButton={isShowSearchButton}
					isOpenSearch={isOpenSearch}
					setIsOpenSearch={setIsOpenSearch}
					tabletDropdownMenu={mobileGlobalNavDropdownMenu()}
				/>
			</div>
			<div
				css={[menuContentStyle, showMenu && css({ display: 'block' })]}
				id="nav-mobile-dropdown-menu"
			>
				<></>
				{isDisplayLoggedInUseDropdown ? (
					<MobileLoggedInUseDropdown
						userInfo={{
							displayName: userInfo.name || '',
							email: userInfo.email || '',
							avatar: userInfo.picture || '',
						}}
						onLogOutClick={onLogOutClick}
						onManageLicensesClick={onManageLicensesClick}
						onManageProfileClick={onManageProfileClick}
						onSwitchAccountClick={onSwitchAccountClick}
					/>
				) : (
					mobileGlobalNavDropdownMenu()
				)}
			</div>
		</nav>
	);
};
